/**
 * Edit mode's word picker (handoff §15.3, «Отметить слова в тексте»): the creation
 * flow's step 2 over the draft body, with the text's gaps pre-marked.
 *
 * It prepares the flow store, then renders `TextFlowWordsPage`, which in this route
 * (it has a `:textId`) offers the creation roles: Card, Gap and Both (ADR-115
 * Amendment 3). The body is analysed by the server (POST /texts/analyse) and the
 * gaps' places come from the server's preview of the draft, mapped onto the analysed
 * words; the client searches nothing (ADR-115 Decision 4). The draft's card marks
 * are put back on the same words.
 *
 * Every way out applies the marks to the edit draft: ‹, «Далее» and the system back
 * alike. Only edit mode's own «Выйти без сохранения?» throws changes away. The marks are
 * applied when this page unmounts, so no route out can skip it.
 *
 * The creation flow's store is borrowed for the picker, not taken: whatever draft it
 * held (a text half-written in step 1 or 2) is set aside on entry and put back when
 * the picker unmounts, by any way out.
 */
import { useEffect, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import api from '../api/client'
import { useLocale } from '../i18n/context'
import { useTextFlowStore } from '../stores/textFlowStore'
import { useTextEditStore } from '../stores/textEditStore'
import { cardMarksFromMarks, marksFromGaps, withCardMarks } from '../utils/textEdit'
import { saveForms } from '../utils/textMarks'
import type { TextAnalysis, TextPreview } from '../types'
import { SecondaryPill } from '../components/texts/ui'
import TextFlowWordsPage from './TextFlowWordsPage'

/**
 * The picker's marks into the edit draft: its gaps as stored gap forms (the server's
 * own spelling where it has answered), plus the gaps the picker could not draw, and
 * its card marks as spans of the body.
 */
function applyMarksToDraft() {
  const flow = useTextFlowStore.getState()
  if (!flow.analysis) return
  const stored = saveForms(flow.body, flow.analysis.words, flow.marks, flow.gapMatch)
    .map((f) => flow.gapMatch?.resolved[f] ?? f.toLowerCase())
  const edit = useTextEditStore.getState()
  edit.setForms([...new Set([...stored, ...edit.pickerCarried])])
  edit.setCardMarks(cardMarksFromMarks(flow.body, flow.analysis, flow.marks))
  edit.setTab('gaps')
}

export default function TextEditWordsPage() {
  const { textId } = useParams<{ textId: string }>()
  const { tr } = useLocale()
  const draft = useTextEditStore((s) => s.draft)
  const ownsDraft = useTextEditStore((s) => s.textId === textId)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [attempt, setAttempt] = useState(0)
  const stashed = useRef<ReturnType<typeof useTextFlowStore.getState> | null>(null)
  // True once the picker's marks are loaded: an unmount before that (a dev double-mount,
  // a failed load) has nothing to apply.
  const prepared = useRef(false)

  useEffect(() => {
    if (!draft || !ownsDraft) return
    let alive = true
    setState('loading')
    stashed.current = useTextFlowStore.getState()
    Promise.all([
      api.post<TextAnalysis>('/api/v1/texts/analyse', { text: draft.body }),
      api.post<TextPreview>(`/api/v1/texts/${textId}/preview`, { body: draft.body, forms: draft.forms }),
    ])
      .then(([analysis, preview]) => {
        if (!alive) return
        const { marks, carried } = marksFromGaps(analysis.data, preview.data.gaps)
        const flow = useTextFlowStore.getState()
        flow.reset() // the borrowed store starts clean; the creation draft is in `stashed`
        flow.setBody(draft.body, 'typed')
        flow.setAnalysis(analysis.data, draft.body)
        // Cards made in an earlier visit are the learner's, not the app's suggestion:
        // «Снять» must not clear them.
        const restored = withCardMarks(analysis.data, marks, draft.cardMarks)
        flow.setMarks({ ...restored, manual: [...new Set([...restored.manual, ...restored.cards])] })
        useTextEditStore.getState().setPickerCarried(carried)
        prepared.current = true
        setState('ready')
      })
      .catch(() => alive && setState('failed'))
    return () => {
      alive = false
      if (prepared.current) applyMarksToDraft()
      prepared.current = false
      if (stashed.current) {
        useTextFlowStore.setState(stashed.current, true)
        stashed.current = null
      }
    }
    // The draft is read once on entry: the picker owns the marks until «Далее».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textId, ownsDraft, attempt])

  // A reload empties the in-memory draft: back to the text.
  if (!draft || !ownsDraft) return <Navigate to={`/texts/${textId}`} replace />
  if (state === 'failed') {
    return (
      <div role="alert" style={{ textAlign: 'center', padding: '64px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
          {tr.textEdit.previewFailed}
        </p>
        <SecondaryPill onClick={() => setAttempt((n) => n + 1)}>{tr.textEdit.retry}</SecondaryPill>
      </div>
    )
  }
  if (state === 'loading') return <div aria-busy="true" style={{ minHeight: 240 }} />
  return <TextFlowWordsPage />
}
